import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import Logo from '../common/Logo';
import TechFigures from '../common/TechFigures';
import { validateBusinessEmail } from '../../utils/emailValidation';
import { setToken } from '../../utils/tokenHelper';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <div style={{ padding: '24px' }}>{children}</div>}
    </div>
  );
}

const industries = [
  'Technology',
  'Healthcare',
  'Finance',
  'Manufacturing',
  'Retail',
  'Education',
  'Real Estate',
  'Energy',
  'Transportation',
  'Entertainment',
  'Food & Beverage',
  'Automotive',
  'Aerospace',
  'Telecommunications',
  'Consulting',
  'Other'
];

const blockedIndustries = [
  'Public Relations',
  'PR Agency',
  'Marketing Agency',
  'Communications Agency',
  'Media Relations',
  'Press Release Service',
  'PR Consulting'
];

const ClientAuth: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailError, setEmailError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Registration form state
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    industry: ''
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
    setEmailError('');
    setFieldErrors({});
    setShowVerificationMessage(false);
    setVerificationEmail('');
  };

  const handleLoginChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLoginData({
      ...loginData,
      [field]: value
    });
    
    // Clear errors when user starts typing
    if (field === 'email' && emailError) {
      setEmailError('');
    }
    if (error) {
      setError('');
    }
    
    // Validate email in real-time
    if (field === 'email' && value) {
      const emailValidation = validateBusinessEmail(value);
      setEmailError(emailValidation.isValid ? '' : emailValidation.error || '');
    }
  };
  
  const handleResendVerification = async () => {
    if (!verificationEmail) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: verificationEmail }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Verification email has been sent. Please check your inbox.');
      } else {
        setError(data.message || 'Failed to resend verification email. Please try again.');
      }
    } catch (error: any) {
      console.error('Resend verification error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
    const value = event.target.value;

    // Always work with the latest snapshot of registerData
    const updatedRegisterData = {
      ...registerData,
      [field]: value
    };
    setRegisterData(updatedRegisterData);
    
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      const newErrors = { ...fieldErrors };
      delete newErrors[field];
      setFieldErrors(newErrors);
    }
    
    // Validate email in real-time
    if (field === 'email') {
      const emailValidation = validateBusinessEmail(value);
      if (!emailValidation.isValid) {
        setEmailError(emailValidation.error || '');
        setFieldErrors({
          ...fieldErrors,
          email: emailValidation.error || ''
        });
      } else {
        setEmailError('');
        const newErrors = { ...fieldErrors };
        delete newErrors.email;
        setFieldErrors(newErrors);
      }
    }
    
    // Validate password match in real-time using the UPDATED values
    if (field === 'password' || field === 'confirmPassword') {
      // Use updatedRegisterData which has the latest value for the current field
      const { password, confirmPassword } = updatedRegisterData;
      
      // Only validate if both fields have values (trim to handle whitespace)
      const passwordTrimmed = password.trim();
      const confirmPasswordTrimmed = confirmPassword.trim();
      
      if (passwordTrimmed && confirmPasswordTrimmed) {
        if (passwordTrimmed !== confirmPasswordTrimmed) {
          setFieldErrors(prev => ({
            ...prev,
            confirmPassword: 'Passwords do not match'
          }));
        } else {
          // Passwords match - clear the error
          setFieldErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.confirmPassword;
            return newErrors;
          });
        }
      } else {
        // If either field is empty, clear the confirmPassword error (don't show error until both are filled)
        if (field === 'confirmPassword' && !confirmPasswordTrimmed) {
          setFieldErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.confirmPassword;
            return newErrors;
          });
        } else if (field === 'password' && !passwordTrimmed && confirmPasswordTrimmed) {
          // If password is cleared but confirmPassword has value, show error
          setFieldErrors(prev => ({
            ...prev,
            confirmPassword: 'Passwords do not match'
          }));
        }
      }
    }
  };

  const validateRegistration = () => {
    const errors: Record<string, string> = {};
    
    if (!registerData.firstName || registerData.firstName.trim() === '') {
      errors.firstName = 'First name is required';
    }
    
    if (!registerData.lastName || registerData.lastName.trim() === '') {
      errors.lastName = 'Last name is required';
    }
    
    if (!registerData.email || registerData.email.trim() === '') {
      errors.email = 'Email is required';
    } else {
      const emailValidation = validateBusinessEmail(registerData.email);
      if (!emailValidation.isValid) {
        errors.email = emailValidation.error || 'Invalid email';
        setEmailError(emailValidation.error || '');
      }
    }
    
    if (!registerData.company || registerData.company.trim() === '') {
      errors.company = 'Company name is required';
    }
    
    if (!registerData.industry || registerData.industry.trim() === '') {
      errors.industry = 'Industry is required';
    }
    
    if (!registerData.password || registerData.password.trim() === '') {
      errors.password = 'Password is required';
    } else if (registerData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    }
    
    if (!registerData.confirmPassword || registerData.confirmPassword.trim() === '') {
      errors.confirmPassword = 'Please confirm your password';
    } else if (registerData.password.trim() !== registerData.confirmPassword.trim()) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    if (blockedIndustries.some(blocked => 
      registerData.industry.toLowerCase().includes(blocked.toLowerCase())
    )) {
      errors.industry = 'PR agencies and marketing agencies are not allowed to use this platform';
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setError('Please fix the errors in the form');
      return false;
    }
    
    return true;
  };

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate business email
    const emailValidation = validateBusinessEmail(loginData.email);
    if (!emailValidation.isValid) {
      setError(emailValidation.error || 'Invalid email');
      setEmailError(emailValidation.error || '');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginData.email,
          password: loginData.password
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store user data and token
        const user = {
          id: data.user.id,
          email: data.user.email,
          firstName: data.user.firstName || data.user.fullName?.split(' ')[0] || 'User',
          lastName: data.user.lastName || data.user.fullName?.split(' ')[1] || '',
          company: data.user.company || '',
          role: data.user.role || 'user',
          industry: data.user.industry || '',
          isEmailVerified: data.user.isEmailVerified || false
        };
        
        setToken(data.token);
        localStorage.setItem('user', JSON.stringify(user));
        
        setSuccess('Login successful! Redirecting...');
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1500);
      } else {
        // Check if email verification is required
        if (data.requiresVerification) {
          setShowVerificationMessage(true);
          setVerificationEmail(loginData.email);
          setError(data.message || 'Please verify your email address before logging in.');
        } else {
          setError(data.message || 'Invalid email or password');
        }
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateRegistration()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: registerData.email,
          password: registerData.password,
          fullName: `${registerData.firstName} ${registerData.lastName}`,
          firstName: registerData.firstName,
          lastName: registerData.lastName,
          company: registerData.company,
          industry: registerData.industry
        }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.requiresVerification) {
          // Email verification required
          setShowVerificationMessage(true);
          setVerificationEmail(registerData.email);
          setSuccess('Registration successful! Please check your email to verify your account.');
          
          // Reset form
          setRegisterData({
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            confirmPassword: '',
            company: '',
            industry: ''
          });
        } else {
          // Auto-login (shouldn't happen with new flow, but handle it)
          const user = {
            id: data.user.id,
            email: data.user.email,
            firstName: registerData.firstName,
            lastName: registerData.lastName,
            company: registerData.company,
            role: data.user.role || 'user',
            industry: registerData.industry
          };
          
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(user));
          
          setSuccess('Registration successful! Redirecting...');
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 1500);
        }
      } else {
        setError(data.message || 'Registration failed. Please try again.');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0927eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Hero Banner with Tech Figures */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: '#0927eb',
          zIndex: 0
        }}
      >
        <TechFigures />
      </div>
      
      <Container maxWidth="sm" style={{ position: 'relative', zIndex: 2 }}>
        <Paper
          elevation={24}
          style={{
            borderRadius: '20px',
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          {/* Header */}
          <div style={{ textAlign: 'center', padding: '40px 40px 20px' }}>
            <Logo size="large" />
            <Typography variant="h4" style={{ marginTop: '20px', fontWeight: 'bold', color: '#0927eb', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              Welcome to <Logo size="inline" animated={false} />
            </Typography>
            <Typography variant="body1" style={{ marginTop: '8px', color: '#666' }}>
              AI-powered press release distribution platform
            </Typography>
          </div>

          {/* Tabs */}
          <div style={{ borderBottom: '1px solid #e0e0e0' }}>
            <Tabs value={tabValue} onChange={handleTabChange} centered>
              <Tab 
                icon={<LoginIcon />} 
                label="Login" 
                iconPosition="start"
                style={{ fontWeight: 'bold' }}
              />
              <Tab 
                icon={<PersonAddIcon />} 
                label="Register" 
                iconPosition="start"
                style={{ fontWeight: 'bold' }}
              />
            </Tabs>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div style={{ padding: '0 24px' }}>
              <Alert severity="error" style={{ marginBottom: '16px' }}>
                {error}
              </Alert>
            </div>
          )}
          {success && (
            <div style={{ padding: '0 24px' }}>
              <Alert severity="success" style={{ marginBottom: '16px' }}>
                {success}
              </Alert>
            </div>
          )}
          {showVerificationMessage && (
            <div style={{ padding: '0 24px' }}>
              <Alert 
                severity="info" 
                style={{ marginBottom: '16px' }}
                action={
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={handleResendVerification}
                    disabled={loading}
                  >
                    Resend
                  </Button>
                }
              >
                <Typography variant="body2" style={{ marginBottom: '8px' }}>
                  Please check your email ({verificationEmail}) to verify your account before logging in.
                </Typography>
                <Typography variant="caption" style={{ display: 'block' }}>
                  Didn't receive the email? Click "Resend" to send a new verification link.
                </Typography>
              </Alert>
            </div>
          )}

          {/* Login Form */}
          <TabPanel value={tabValue} index={0}>
            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={loginData.email}
                onChange={handleLoginChange('email')}
                margin="normal"
                required
                error={!!emailError}
                helperText={emailError}
                InputProps={{
                  startAdornment: <EmailIcon style={{ marginRight: '8px', color: '#666' }} />
                }}
                style={{ marginBottom: '16px' }}
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={loginData.password}
                onChange={handleLoginChange('password')}
                margin="normal"
                required
                InputProps={{
                  startAdornment: <LockIcon style={{ marginRight: '8px', color: '#666' }} />
                }}
                style={{ marginBottom: '24px' }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                style={{
                  backgroundColor: '#000000',
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '12px',
                  fontSize: '16px'
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
              </Button>
            </form>
          </TabPanel>

          {/* Registration Form */}
          <TabPanel value={tabValue} index={1}>
            <form onSubmit={handleRegister}>
              <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <TextField
                fullWidth
                label="First Name"
                value={registerData.firstName}
                onChange={handleRegisterChange('firstName')}
                required
                error={!!fieldErrors.firstName}
                helperText={fieldErrors.firstName}
                InputProps={{
                  startAdornment: <PersonIcon style={{ marginRight: '8px', color: '#666' }} />
                }}
              />
              <TextField
                fullWidth
                label="Last Name"
                value={registerData.lastName}
                onChange={handleRegisterChange('lastName')}
                required
                error={!!fieldErrors.lastName}
                helperText={fieldErrors.lastName}
              />
              </div>
              
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={registerData.email}
                onChange={handleRegisterChange('email')}
                margin="normal"
                required
                error={!!emailError}
                helperText={emailError}
                InputProps={{
                  startAdornment: <EmailIcon style={{ marginRight: '8px', color: '#666' }} />
                }}
                style={{ marginBottom: '16px' }}
              />
              
              <TextField
                fullWidth
                label="Company Name"
                value={registerData.company}
                onChange={handleRegisterChange('company')}
                margin="normal"
                required
                error={!!fieldErrors.company}
                helperText={fieldErrors.company}
                InputProps={{
                  startAdornment: <BusinessIcon style={{ marginRight: '8px', color: '#666' }} />
                }}
                style={{ marginBottom: '16px' }}
              />

              <FormControl fullWidth margin="normal" style={{ marginBottom: '16px' }} error={!!fieldErrors.industry}>
                <InputLabel>Industry</InputLabel>
                <Select
                  value={registerData.industry}
                  onChange={handleRegisterChange('industry')}
                  label="Industry"
                  required
                >
                  {industries.map((industry) => (
                    <MenuItem key={industry} value={industry}>
                      {industry}
                    </MenuItem>
                  ))}
                </Select>
                {fieldErrors.industry && (
                  <Typography variant="caption" color="error" style={{ marginTop: '4px', marginLeft: '14px' }}>
                    {fieldErrors.industry}
                  </Typography>
                )}
              </FormControl>

              <TextField
                fullWidth
                label="Password"
                type="password"
                value={registerData.password}
                onChange={handleRegisterChange('password')}
                margin="normal"
                required
                error={!!fieldErrors.password}
                helperText={fieldErrors.password || 'Minimum 8 characters'}
                InputProps={{
                  startAdornment: <LockIcon style={{ marginRight: '8px', color: '#666' }} />
                }}
                style={{ marginBottom: '16px' }}
              />
              
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={registerData.confirmPassword}
                onChange={handleRegisterChange('confirmPassword')}
                margin="normal"
                required
                error={!!fieldErrors.confirmPassword}
                helperText={fieldErrors.confirmPassword}
                style={{ marginBottom: '24px' }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                style={{
                  backgroundColor: '#000000',
                  color: 'white',
                  fontWeight: 'bold',
                  padding: '12px',
                  fontSize: '16px'
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
              </Button>
            </form>
          </TabPanel>

          {/* Footer */}
          <div style={{ padding: '20px', textAlign: 'center', borderTop: '1px solid #eee' }}>
            <Typography variant="body2" style={{ color: '#666' }}>
              By continuing, you agree to our Terms of Service and Privacy Policy
            </Typography>
          </div>
        </Paper>
      </Container>
    </div>
  );
};

export default ClientAuth;
